/**
 * Prose settings (`type: "richtext"`) are authored as HTML in the builder's
 * editor and rendered by the storefront with `v-html` + `.dk-rte`.
 *
 * Every one of them was a plain `textarea` first, so a live theme document holds
 * PLAIN TEXT for the same key — often with newlines, which the old renderers
 * either preserved with `white-space: pre-line` or silently collapsed. Handing
 * that string to `v-html` would join the lines, and handing it to the editor
 * would be worse: Tiptap parses it as one paragraph, so the first keystroke
 * would serialize the joined version back over the seller's line breaks.
 *
 * `toRichHtml` is the one conversion, applied at READ time on both sides — the
 * storefront renders through it and the builder feeds the editor through it — so
 * the two always agree and NO stored document is rewritten. That is deliberate:
 * a migration over every section and block of every published theme has no undo,
 * and this needs none. A value is upgraded in storage only when a seller
 * actually edits it, and the function is idempotent, so the upgraded value
 * passes through untouched forever after.
 */
/**
 * Does this string already carry markup we authored?
 *
 * The list is the tags the editor's schema can emit, NOT "does it contain a
 * `<`" — prose legitimately contains `5 < 10` and `<3`, and treating those as
 * markup would hand raw angle brackets to `v-html` and drop the rest of the
 * line. Anything not matched here is escaped, which is what the plain-text
 * renderers did before.
 */
const RICH_TAG = /<\/?(?:p|br|h[1-6]|ul|ol|li|blockquote|strong|em|b|i|u|s|a|span|div|figure|img|table)\b[^>]*>/i;
export function isRichHtml(value) {
    return RICH_TAG.test(value);
}
/** Escape a plain-text run for inclusion in HTML. */
export function escapeHtml(value) {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}
/**
 * An editor that has been emptied emits `<p></p>` rather than `""`, so a value
 * that renders as nothing has two spellings. Both must read as blank, or a
 * `v-if="text"` guard would keep an empty block on the page and the builder
 * would autosave a "change" the seller did not make.
 */
export function isBlankRichHtml(value) {
    if (value == null)
        return true;
    const s = String(value)
        .replace(/<br\s*\/?>/gi, "")
        .replace(/<\/?(?:p|div|h[1-6])\b[^>]*>/gi, "")
        .replace(/&nbsp;/gi, " ")
        .trim();
    return s === "";
}
/**
 * Normalize a prose setting to the HTML both the storefront and the editor read.
 *
 * Idempotent: HTML passes through unchanged, so calling it on an already-edited
 * value costs one regex test.
 */
export function toRichHtml(value) {
    if (value == null)
        return "";
    const raw = String(value);
    if (raw.trim() === "")
        return "";
    if (isRichHtml(raw))
        return raw;
    // A blank line separates paragraphs and a single newline is a line break —
    // the same reading every rich-text editor and Markdown renderer uses, and the
    // one that keeps `white-space: pre-line` output looking identical.
    return raw
        .split(/\r?\n[ \t]*\r?\n/)
        .map(para => para.trim())
        .filter(para => para !== "")
        .map(para => `<p>${escapeHtml(para).replace(/\r?\n/g, "<br>")}</p>`)
        .join("");
}
/**
 * Visible text of a prose setting, for the places that need a string rather than
 * markup — a `title` attribute, an `aria-label`, an OG description, a length
 * check. Never feed this to `v-html`.
 */
export function richTextToPlain(value) {
    if (value == null)
        return "";
    const raw = String(value);
    if (!isRichHtml(raw))
        return raw;
    return raw
        .replace(/<\/(?:p|div|h[1-6]|li|blockquote)>/gi, "\n")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, "&")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
//# sourceMappingURL=richtext.js.map